import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, HelpCircle, MinusCircle, ChevronDown, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { LoadingState, PermissionDenied } from "@/components/states";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";

// Status → icon + accessible label (never colour-only).
const STATUS = {
  healthy: { label: "Healthy", icon: CheckCircle2, cls: "text-success", dot: "bg-success", ring: "border-success/30 bg-success/5" },
  needs_attention: { label: "Needs attention", icon: AlertTriangle, cls: "text-error", dot: "bg-error", ring: "border-error/30 bg-error/5" },
  could_not_verify: { label: "Could not verify", icon: HelpCircle, cls: "text-warning", dot: "bg-warning", ring: "border-warning/30 bg-warning/5" },
  not_configured: { label: "Not configured", icon: MinusCircle, cls: "text-muted-foreground", dot: "bg-muted-foreground", ring: "border-border bg-muted/30" },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.could_not_verify;
  const Icon = s.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium", s.ring, s.cls)}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {s.label}
    </span>
  );
}

function HealthCard({ check, isAdmin }) {
  const [open, setOpen] = useState(false);
  const s = STATUS[check.status] || STATUS.could_not_verify;
  return (
    <Card className={cn("p-4 shadow-soft", s.ring)} data-testid={`health-${check.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className={cn("mt-1 h-2.5 w-2.5 shrink-0 rounded-full", s.dot)} aria-hidden="true" />
          <div>
            <div className="text-sm font-semibold text-foreground">{check.name}</div>
            <div className="text-xs text-muted-foreground">{check.message}</div>
          </div>
        </div>
        <StatusPill status={check.status} />
      </div>
      {isAdmin && check.detail && (
        <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`health-${check.key}-details`}>
              <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} /> View details
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="mt-1 rounded bg-muted/50 px-2 py-1 font-mono text-[11px] text-muted-foreground">{check.detail}</p>
          </CollapsibleContent>
        </Collapsible>
      )}
    </Card>
  );
}

export default function SystemHealth() {
  const perms = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const check = useCallback((manual) => {
    setLoading(true);
    apiFetch("/system/health")
      .then((d) => { setData(d); if (manual) toast.success("Health re-checked", { description: `Overall: ${d.overall.replace(/_/g, " ")}` }); })
      .catch((e) => { if ((e.message || "").includes("permission")) setDenied(true); else toast.error(e.message); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { check(false); }, [check]);

  if (denied) return <PermissionDenied message="You need the “View System Health” permission to view this area." />;
  if (loading && !data) return <LoadingState label="Checking system health…" />;

  const lastChecked = data?.checked_at ? new Date(data.checked_at).toLocaleString() : "—";

  return (
    <div className="animate-fade-up" data-testid="system-health">
      <PageHeader eyebrow="Settings" title="System Health"
        subtitle="Live status of the services ContentOS depends on. Statuses reflect real checks — nothing is faked."
        actions={<Button variant="outline" className="gap-2" onClick={() => check(true)} disabled={loading} data-testid="health-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Check again
        </Button>} />

      {data && (
        <>
          <Card className="mb-5 flex flex-wrap items-center justify-between gap-3 p-4 shadow-soft" data-testid="health-overall">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><HeartPulse className="h-5 w-5" /></span>
              <div>
                <div className="text-sm font-semibold">Overall status</div>
                <div className="text-xs text-muted-foreground">Last checked {lastChecked}</div>
              </div>
            </div>
            <StatusPill status={data.overall} />
          </Card>

          <h3 className="mb-3 font-heading text-lg font-semibold">Core services</h3>
          <div className="mb-6 grid gap-3 sm:grid-cols-2">
            {data.core.map((c) => <HealthCard key={c.key} check={c} isAdmin={perms.is_admin} />)}
          </div>

          <h3 className="mb-1 font-heading text-lg font-semibold">Integrations</h3>
          <p className="mb-3 text-xs text-muted-foreground">These connect in later releases. Until then they show as not configured — never a fake healthy state.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.integrations.map((c) => <HealthCard key={c.key} check={c} isAdmin={perms.is_admin} />)}
          </div>
        </>
      )}
    </div>
  );
}
